import { useMemo, useState, forwardRef, PropsWithChildren } from 'react';
import styles from './SearchBar.module.scss';
import SearchIcon from 'assets/search.svg';
import CloseIcon from 'assets/close.svg';
import { debounce } from 'utils/debounce';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'hooks/useTranslation';

type Props = {
  placeholder: string;
  searchQuery: string | null;
  setQuery(newQuery: string | null): void;
  inputId: string;
};

export const SearchBar = forwardRef<HTMLInputElement, PropsWithChildren<Props>>(
  function SearchBar({ placeholder, searchQuery, setQuery, inputId }, ref) {
    const t = useTranslation();
    const debouncedSetQuery = useMemo(() => debounce(setQuery), [setQuery]);
    const [localQuery, setLocalQuery] = useState(searchQuery || '');

    return (
      <div className={styles.inputContainer}>
        <SearchIcon className={styles.inputIcon} aria-hidden />
        <VisuallyHidden asChild>
          <label htmlFor={inputId}>{t('search')}</label>
        </VisuallyHidden>
        <input
          value={localQuery}
          type="search"
          id={inputId}
          className={styles.searchBar}
          placeholder={placeholder}
          autoComplete="off"
          onInput={(e) => {
            setLocalQuery(e.currentTarget.value);
            debouncedSetQuery(e.currentTarget.value);
          }}
          ref={ref}
        />
        {localQuery.length > 0 ? (
          <button
            className={styles.clearButton}
            onClick={() => {
              setLocalQuery('');
              setQuery(null);
            }}
            type="button"
          >
            <CloseIcon aria-hidden className={styles.clearIcon} />
            <VisuallyHidden>{t('clearInput')}</VisuallyHidden>
          </button>
        ) : null}
      </div>
    );
  }
);
